import React from "react";
import {
  AccessTimeOutlined,
  AccountBalanceWalletOutlined,
  AccountCircleOutlined,
  AttachMoneyOutlined,
  BadgeOutlined,
  BusinessOutlined,
  CheckCircleOutlined,
  DescriptionOutlined,
  EventOutlined,
  InfoOutlined,
  ReceiptLongOutlined,
  SourceOutlined,
  StorefrontOutlined,
  UpdateOutlined,
} from "@mui/icons-material";
import { Box, Grid } from "@mui/material";
import { getLedgerSourceInfo } from "utils/accountLedgerformatValue";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import InformationTile from "components/Shared/InformationTile";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import { localizeDateMed } from "utils/dateFormats";
import { formatAmount } from "utils/formatString";
import ReactHtmlParser from "html-react-parser";

const AccountLedgerBasicDetail = ({
  loadingAccountLedger,
  accountLedgerDetail,
}) => {
  const sourceInfo = getLedgerSourceInfo(
    accountLedgerDetail?.originalSource,
    accountLedgerDetail?.originalSourceRef
  );

  if (loadingAccountLedger) return <InfoGridSkeleton />;

  // Helper function to format originalSource label
  const formatOriginalSourceLabel = (source) => {
    if (!source) return "Original Source";
    return source.replace(/([A-Z])/g, " $1").trim();
  };

  return (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item md={3}>
          <InformationTile
            icon={() => <BusinessOutlined color="primary" />}
            title="Account"
            subtitle={accountLedgerDetail?.account?.name ?? "N/A"}
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <BadgeOutlined color="primary" />}
            title="Entity"
            subtitle={accountLedgerDetail?.entity?.name ?? "N/A"}
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <StorefrontOutlined color="primary" />}
            title="Party"
            subtitle={
              accountLedgerDetail?.vendor ? (
                <OpenInNewTab
                  label={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: "100%",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.9rem",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {accountLedgerDetail?.vendor?.vendor?.name}
                      </span>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        Vendor
                      </div>
                    </div>
                  }
                  link={`/app/crm/vendor-profile/${accountLedgerDetail?.vendor?._id}/detail?tab=vendor-ledger`}
                />
              ) : accountLedgerDetail?.contractor ? (
                <OpenInNewTab
                  label={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: "100%",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.9rem",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {accountLedgerDetail?.contractor?.name}
                      </span>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        Contractor
                      </div>
                    </div>
                  }
                  link={`/app/crm/contractor/${accountLedgerDetail?.contractor?._id}/detail?tab=contractor-ledger`}
                />
              ) : accountLedgerDetail?.customer ? (
                <OpenInNewTab
                  label={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: "100%",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.9rem",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {accountLedgerDetail?.customer?.customerUser?.name}
                      </span>
                      <div style={{ fontSize: "0.75rem", color: "#666" }}>
                        Customer
                      </div>
                    </div>
                  }
                  link={`/app/crm/customerProfile/${accountLedgerDetail?.customer?._id}/detail?tab=customer-ledger`}
                />
              ) : (
                "N/A"
              )
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <AccountBalanceWalletOutlined color="primary" />}
            title="Payment Account"
            subtitle={
              accountLedgerDetail?.paymentAccount?._id ? (
                <OpenInNewTab
                  label={accountLedgerDetail?.paymentAccount?.accountName}
                  link={`/app/crm/payment-account/${accountLedgerDetail?.paymentAccount?._id}/detail`}
                />
              ) : (
                "N/A"
              )
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <EventOutlined color="primary" />}
            title="Transaction Date"
            subtitle={
              accountLedgerDetail?.transactionDate
                ? localizeDateMed(accountLedgerDetail?.transactionDate)
                : "N/A"
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <SourceOutlined color="primary" />}
            title={formatOriginalSourceLabel(accountLedgerDetail?.originalSource)}
            subtitle={
              sourceInfo?._id && sourceInfo?.link ? (
                <OpenInNewTab
                  label={sourceInfo?.label}
                  link={`/app/crm/${sourceInfo?.link}/${sourceInfo?._id}/detail`}
                />
              ) : (
                "N/A"
              )
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <AttachMoneyOutlined color="primary" />}
            title="Amount"
            subtitle={
              accountLedgerDetail?.amount
                ? `${
                    accountLedgerDetail?.entity?.currency?.code ?? "USD"
                  } ${formatAmount(accountLedgerDetail?.amount)}`
                : "N/A"
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <DescriptionOutlined color="primary" />}
            title="Reference"
            subtitle={accountLedgerDetail?.reference ?? "N/A"}
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <ReceiptLongOutlined color="primary" />}
            title="Type"
            subtitle={accountLedgerDetail?.type ?? "N/A"}
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <AccountCircleOutlined color="primary" />}
            title="Added By"
            subtitle={accountLedgerDetail?.addedBy?.name ?? "N/A"}
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <CheckCircleOutlined color="primary" />}
            title="Status"
            subtitle={accountLedgerDetail?.status ?? "N/A"}
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <AccessTimeOutlined color="primary" />}
            title="Created At"
            subtitle={
              accountLedgerDetail?.createdAt
                ? localizeDateMed(accountLedgerDetail?.createdAt)
                : "N/A"
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <UpdateOutlined color="primary" />}
            title="Updated At"
            subtitle={
              accountLedgerDetail?.updatedAt
                ? localizeDateMed(accountLedgerDetail?.updatedAt)
                : "N/A"
            }
          />
        </Grid>
        <Grid item md={12}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Description"
            subtitle={
              <div>
                {accountLedgerDetail?.description
                  ? ReactHtmlParser(accountLedgerDetail?.description)
                  : "N/A"}
              </div>
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountLedgerBasicDetail;